import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, HelpCircle } from "lucide-react";

const FAQS = [
  { q: "What is Kiddo+?", a: "Kiddo+ is a child development platform created by occupational therapists to help parents understand, support, and track their child's development. It provides personalised guidance, home activities, expert-backed strategies, and progress tracking tailored to your child's unique needs." },
  { q: "Who is Kiddo+ for?", a: "Kiddo+ is designed for parents, caregivers, teachers, and therapists supporting children aged 3-12 years. It can support children with Autism Spectrum Disorder (ASD), ADHD, Global Developmental Delay (GDD), Sensory Processing Difficulties, Speech and Language Delays, Learning Difficulties, Dyslexia, Dyspraxia / Developmental Coordination Disorder (DCD), Intellectual Disability, Social Communication Difficulties, Emotional and Behavioural Challenges, and Executive Function Difficulties. It is also valuable for parents who simply want to better understand their child's development." },
  { q: "Is Kiddo+ free?", a: "Most features in Kiddo+ are completely free for parents. You can access personalised guidance, home-based activities, and developmental insights at no cost. For families who want additional support, advanced tools, and premium resources, we also offer a Premium Plan with enhanced features and benefits." },
  { q: "How does Kiddo+ personalise recommendations?", a: "When you complete your child's profile and developmental assessment, Kiddo+ analyses information about your child's strengths, challenges, developmental skills, interests, goals, and diagnoses (if applicable). Based on this, Kiddo+ recommends activities, strategies, and guidance specifically suited to your child." },
  { q: "Do I need a diagnosis to use Kiddo+?", a: "No. Many children experience developmental, learning, communication, or behavioural challenges without a formal diagnosis. Kiddo+ can provide support regardless of whether your child has received an official diagnosis." },
];

function FaqItem({ q, a, open, onToggle, idx }) {
  return (
    <li
      className={`bg-white border ${open ? "border-coral/30 shadow-soft-md" : "border-plum/10"} rounded-2xl overflow-hidden transition-colors`}
      data-testid={`faq-item-${idx}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 sm:px-6 sm:py-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2"
      >
        <span className="font-heading text-base sm:text-lg text-inkdark leading-snug">{q}</span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-coral transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          strokeWidth={2.4}
        />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="px-5 sm:px-6 pb-5 text-sm text-inkmid leading-relaxed">{a}</p>
        </div>
      </div>
    </li>
  );
}

/** Single-open FAQ accordion (top 5) with a "See all FAQs" link to /faqs. */
export default function FaqSection() {
  const [openIdx, setOpenIdx] = useState(-1);
  return (
    <section className="relative bg-cream py-16 lg:py-20 overflow-hidden" id="faq">
      <div aria-hidden="true" className="pointer-events-none absolute -top-12 -left-12 w-48 h-48 bg-coral/10 rounded-full blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-1/4 w-56 h-56 bg-mint/15 rounded-full blur-3xl" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-10 sm:mb-12 kpp-reveal" data-reveal>
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-coral/10 text-coral rounded-full text-[11px] font-bold uppercase tracking-widest mb-5">
            <HelpCircle className="w-3.5 h-3.5" /> FAQs
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
            Questions, <span className="text-coral">answered.</span>
          </h2>
          <p className="mt-4 text-inkmid text-sm sm:text-base leading-relaxed">
            The most common things parents ask before getting started.
          </p>
        </div>

        <ul className="space-y-3 kpp-reveal" data-reveal data-stagger data-testid="faq-list">
          {FAQS.map((f, i) => (
            <FaqItem
              key={f.q}
              q={f.q}
              a={f.a}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              idx={i}
            />
          ))}
        </ul>

        <div className="mt-8 text-center">
          <Link
            to="/faqs"
            data-testid="faq-see-all-btn"
            className="inline-flex items-center gap-2 bg-plum hover:bg-plum/90 text-white rounded-full px-6 py-2.5 text-sm font-bold transition-colors shadow-soft"
          >
            See all FAQs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
